import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { ChevronLeft, ChevronDown, Minus, Plus, Check, CreditCard, UserPlus, X, Search, User, Clock } from 'lucide-react';
import { PRODUCTOS } from '../constants/productos';
import { fmtQty, buildTicketText, sendTicketToBridge } from '../utils/ticketPrint';
import { fmtMoney } from '../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP = (allowHalf) => allowHalf ? 0.5 : 1;
const parseQty = (val, allowHalf) => {
  if (allowHalf) {
    const f = parseFloat(val) || 0;
    return Math.round(f * 2) / 2;
  }
  return parseInt(val) || 0;
};

export function RegistrarVenta() {
  const { user } = useAuth();
  const { isOnline, addOfflineItem, cacheData, getCachedData } = useOffline();
  const { prefs } = usePreferences();
  const navigate = useNavigate();

  const [inventario, setInventario] = useState({});
  const [precios, setPrecios] = useState({});
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Credit sale state
  const [esCredito, setEsCredito] = useState(false);
  // Las vendedoras no registran crédito: lo solicitan y administración lo autoriza
  const esAdmin = user?.role === 'admin' || user?.role === 'propietario';
  const solicitaCredito = esCredito && !esAdmin;
  const [confirmarPago, setConfirmarPago] = useState(false);

  // Client selection (independent of credit)
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [showClienteSelector, setShowClienteSelector] = useState(false);
  const [showNuevoCliente, setShowNuevoCliente] = useState(false);
  const [nuevoClienteNombre, setNuevoClienteNombre] = useState('');
  const [nuevoClienteTelefono, setNuevoClienteTelefono] = useState('');
  const [creandoCliente, setCreandoCliente] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: diaCerradoData } = await axios.get(`${API}/dia-cerrado/${user.punto_venta_id}`);
      if (diaCerradoData.cerrado) {
        setError('El día ya fue cerrado. No se pueden registrar más ventas.');
        setLoading(false);
        return;
      }

      const [invRes, preciosRes, clientesRes] = await Promise.all([
        axios.get(`${API}/inventario/actual/${user.punto_venta_id}`),
        axios.get(`${API}/precios/${user.punto_venta_id}`),
        axios.get(`${API}/clientes/${user.punto_venta_id}`),
      ]);

      setInventario(invRes.data.productos || {});
      setPrecios(preciosRes.data.precios || {});
      cacheData(`precios_${user.punto_venta_id}`, preciosRes.data.precios);
      setClientes((clientesRes.data || []).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
    } catch (error) {
      const cached = getCachedData(`precios_${user.punto_venta_id}`);
      if (cached) setPrecios(cached);
    } finally {
      setLoading(false);
    }
  };

  const handleIncrement = (clave, allowHalf) => {
    const currentQty = quantities[clave] || 0;
    const available = inventario[clave] || 0;
    const step = STEP(allowHalf);
    if (currentQty + step <= available) {
      setQuantities(prev => ({ ...prev, [clave]: Math.round((currentQty + step) * 2) / 2 }));
    }
  };

  const handleDecrement = (clave, allowHalf) => {
    const currentQty = quantities[clave] || 0;
    const step = STEP(allowHalf);
    if (currentQty > 0) {
      const next = Math.max(0, Math.round((currentQty - step) * 2) / 2);
      setQuantities(prev => ({ ...prev, [clave]: next }));
    }
  };

  const getTotalPiezas = () => Object.values(quantities).reduce((sum, qty) => sum + (qty || 0), 0);

  const getTotalMonto = () => {
    let total = 0;
    for (const [clave, qty] of Object.entries(quantities)) {
      total += (precios[clave] || 0) * qty;
    }
    return total;
  };

  const clienteSeleccionado = clientes.find(c => c.id === clienteId) || null;

  const selectCliente = (id) => {
    setClienteId(id);
    setBusquedaCliente('');
    setShowClienteSelector(false);
    setShowNuevoCliente(false);
  };

  const clearCliente = () => {
    setClienteId('');
    setBusquedaCliente('');
    setShowClienteSelector(false);
  };

  const handleCrearCliente = async () => {
    if (!nuevoClienteNombre.trim()) return;
    setCreandoCliente(true);
    try {
      const { data } = await axios.post(`${API}/clientes`, {
        nombre: nuevoClienteNombre.trim(),
        telefono: nuevoClienteTelefono.trim() || null,
      });
      setClientes(prev => [...prev, data].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
      selectCliente(data.id);
      setNuevoClienteNombre('');
      setNuevoClienteTelefono('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear cliente');
    } finally {
      setCreandoCliente(false);
    }
  };

  const printTicket = async (ventaData) => {
    if (!prefs.printerEnabled || !prefs.printerIp?.trim() || !prefs.bridgeUrl?.trim()) return;
    try {
      const clienteNombre = clienteSeleccionado?.nombre || 'Público en General';
      const now = new Date().toLocaleString('es-MX');
      const infoLines = [`Fecha: ${now}`];
      if (user.punto_venta_nombre) infoLines.push(`Punto: ${user.punto_venta_nombre}`);
      infoLines.push(`Vendedor: ${user.nombre || user.username}`, `Cliente: ${clienteNombre}`);
      const lines = buildTicketText({
        infoLines,
        productos: ventaData.productos,
        preciosSnapshot: ventaData.precios_snapshot,
        pagoLabel: ventaData.es_credito ? 'A crédito' : 'Efectivo',
      });
      await sendTicketToBridge(prefs, lines);
    } catch { /* print errors are non-fatal */ }
  };

  const handleSubmit = () => {
    if (getTotalPiezas() === 0) {
      setError('Selecciona al menos un producto');
      return;
    }
    if (esCredito && !clienteId) {
      setError('Selecciona un cliente para la venta a crédito');
      return;
    }

    setError('');

    // Con cliente y sin crédito: confirmar que de verdad es de contado
    // (una venta a crédito capturada como contado sale como faltante en el corte)
    if (clienteId && !esCredito) {
      setConfirmarPago(true);
      return;
    }
    enviarVenta(esCredito);
  };

  const elegirPago = (credito) => {
    setConfirmarPago(false);
    if (credito) setEsCredito(true);
    enviarVenta(credito);
  };

  const enviarVenta = async (credito) => {
    if (credito && !esAdmin) {
      if (!isOnline) {
        setError('Necesitas conexión a internet para solicitar una venta a crédito');
        return;
      }
      setSubmitting(true);
      try {
        await axios.post(`${API}/solicitudes-credito`, {
          tipo: 'venta_credito',
          cliente_id: clienteId,
          productos: quantities,
        });
        setSuccess(true);
        setTimeout(() => navigate('/pos'), 2500);
      } catch (error) {
        setError(error.response?.data?.detail || 'Error al enviar la solicitud');
      }
      setSubmitting(false);
      return;
    }

    setSubmitting(true);

    const data = {
      punto_venta_id: user.punto_venta_id,
      tipo: 'venta',
      productos: quantities,
      precios_snapshot: precios,
      es_credito: credito,
      cliente_id: clienteId || null,
      fecha: new Date().toISOString().split('T')[0],
    };

    if (isOnline) {
      try {
        await axios.post(`${API}/movimientos`, data);
        printTicket(data);
        setSuccess(true);
        setTimeout(() => navigate('/pos'), 1500);
      } catch (error) {
        setError(error.response?.data?.detail || 'Error al registrar venta');
      }
    } else {
      addOfflineItem('movimientos', data);
      printTicket(data);
      setSuccess(true);
      setTimeout(() => navigate('/pos'), 1500);
    }

    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (success && solicitaCredito) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
          <Clock className="w-10 h-10 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900 mb-2">Solicitud enviada a administración</h1>
        <p className="text-4xl font-bold text-blue-600">{fmtMoney(getTotalMonto())}</p>
        <p className="text-zinc-500 mt-2">{fmtQty(getTotalPiezas())} piezas a crédito para {clienteSeleccionado?.nombre}</p>
        <p className="text-sm text-zinc-500 mt-4 max-w-xs">
          Las piezas quedan apartadas. La venta se registra cuando administración la autorice.
          No podrás cerrar tu turno mientras esté pendiente.
        </p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900 mb-2">
          {esCredito ? 'Venta a crédito registrada' : 'Venta registrada'}
        </h1>
        <p className="text-4xl font-bold text-green-600">{fmtMoney(getTotalMonto())}</p>
        <p className="text-zinc-500 mt-2">{fmtQty(getTotalPiezas())} piezas vendidas</p>
        <p className={`mt-1 text-sm ${clienteSeleccionado ? 'text-blue-600' : 'text-zinc-400'}`}>
          {clienteSeleccionado ? clienteSeleccionado.nombre : 'Público en General'}
        </p>
      </div>
    );
  }

  const filtrados = busquedaCliente.trim()
    ? clientes.filter(c => c.nombre.toLowerCase().includes(busquedaCliente.toLowerCase()))
    : clientes;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="registrar-venta-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/pos')}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-zinc-900">Registrar Venta</h1>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Product List */}
      <div className="flex-1 overflow-auto px-4 py-4">
        <div className="space-y-1">
          {PRODUCTOS.map((producto) => {
            const qty = quantities[producto.clave] || 0;
            const available = inventario[producto.clave] || 0;
            const precio = precios[producto.clave] || 0;

            return (
              <div
                key={producto.clave}
                className="flex items-center justify-between py-4 border-b border-zinc-100"
                data-testid={`producto-${producto.clave}`}
              >
                <div>
                  <p className="text-lg font-medium text-zinc-900">{producto.nombre}</p>
                  <p className="text-sm text-zinc-500">
                    {fmtMoney(precio)} • Disp: {available}
                    {producto.allowHalf && <span className="ml-1 text-amber-600 font-medium">· ½</span>}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDecrement(producto.clave, producto.allowHalf)}
                    disabled={qty === 0}
                    className="w-12 h-12 flex items-center justify-center border border-zinc-300 rounded-lg text-zinc-600 active:bg-zinc-100 disabled:opacity-30"
                    data-testid={`btn-minus-${producto.clave}`}
                  >
                    <Minus className="w-5 h-5" />
                  </button>

                  <input
                    type="number"
                    inputMode={producto.allowHalf ? 'decimal' : 'numeric'}
                    min="0"
                    max={available}
                    step={producto.allowHalf ? '0.5' : '1'}
                    value={qty === 0 ? '' : qty}
                    onChange={e => {
                      const val = Math.min(available, Math.max(0, parseQty(e.target.value, producto.allowHalf)));
                      setQuantities(prev => ({ ...prev, [producto.clave]: val }));
                    }}
                    onFocus={e => e.target.select()}
                    placeholder="0"
                    className={`w-16 h-12 text-center text-2xl font-bold border rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${qty > 0 ? 'text-green-600 border-green-300 bg-green-50' : 'text-zinc-400 border-zinc-200 bg-white'}`}
                    data-testid={`input-qty-${producto.clave}`}
                  />

                  <button
                    onClick={() => handleIncrement(producto.clave, producto.allowHalf)}
                    disabled={qty >= available}
                    className="w-12 h-12 flex items-center justify-center border border-zinc-300 rounded-lg text-zinc-600 active:bg-zinc-100 disabled:opacity-30"
                    data-testid={`btn-plus-${producto.clave}`}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Cliente */}
        <div className="mt-6 pt-4 border-t border-zinc-200">
          <p className="text-xs font-medium text-zinc-500 tracking-wide mb-2">CLIENTE</p>

          {clienteSeleccionado ? (
            <div className="flex items-center justify-between px-4 py-3 border border-zinc-300 rounded-xl bg-zinc-50" data-testid="select-cliente">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-zinc-400 shrink-0" />
                <div>
                  <p className="font-medium text-zinc-900 text-sm">{clienteSeleccionado.nombre}</p>
                  {clienteSeleccionado.saldo < 0 && (
                    <p className="text-xs text-red-500">Debe {fmtMoney(Math.abs(clienteSeleccionado.saldo))}</p>
                  )}
                </div>
              </div>
              <button onClick={clearCliente} className="text-zinc-400 hover:text-zinc-600 ml-2">
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : showClienteSelector ? (
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="text"
                  value={busquedaCliente}
                  onChange={e => setBusquedaCliente(e.target.value)}
                  placeholder="Buscar cliente por nombre..."
                  autoFocus
                  autoComplete="off"
                  className="w-full h-11 pl-9 pr-4 border border-zinc-300 rounded-xl text-sm text-zinc-900"
                  data-testid="select-cliente"
                />
              </div>
              <div className="border border-zinc-200 rounded-xl overflow-hidden mt-1" style={{ maxHeight: 176, overflowY: 'auto' }}>
                <button
                  onClick={() => { clearCliente(); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-50 border-b border-zinc-100 text-left"
                >
                  <User className="w-4 h-4 text-zinc-300" />
                  <span className="text-zinc-400 text-sm italic">Público en General</span>
                </button>
                {filtrados.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-zinc-400">Sin resultados</p>
                ) : (
                  filtrados.map(c => (
                    <button
                      key={c.id}
                      onClick={() => selectCliente(c.id)}
                      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-zinc-50 border-b border-zinc-100 last:border-0 text-left"
                    >
                      <span className="font-medium text-zinc-900 text-sm">{c.nombre}</span>
                      {c.saldo < 0 && <span className="text-red-500 text-xs">debe {fmtMoney(Math.abs(c.saldo))}</span>}
                    </button>
                  ))
                )}
              </div>

              {!showNuevoCliente ? (
                <button
                  onClick={() => setShowNuevoCliente(true)}
                  className="flex items-center gap-2 text-blue-600 text-sm font-medium mt-2"
                  data-testid="btn-nuevo-cliente"
                >
                  <UserPlus className="w-4 h-4" />
                  Nuevo cliente
                </button>
              ) : (
                <div className="bg-blue-50 rounded-xl p-4 space-y-3 border border-blue-200 mt-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-blue-700">Nuevo cliente</p>
                    <button onClick={() => setShowNuevoCliente(false)}><X className="w-4 h-4 text-blue-400" /></button>
                  </div>
                  <input
                    type="text"
                    value={nuevoClienteNombre}
                    onChange={e => setNuevoClienteNombre(e.target.value)}
                    placeholder="Nombre del cliente"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm"
                    data-testid="input-nuevo-cliente-nombre"
                  />
                  <input
                    type="tel"
                    value={nuevoClienteTelefono}
                    onChange={e => setNuevoClienteTelefono(e.target.value)}
                    placeholder="Teléfono (opcional)"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={handleCrearCliente}
                    disabled={!nuevoClienteNombre.trim() || creandoCliente}
                    className="w-full h-11 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    {creandoCliente ? 'Guardando...' : 'Guardar cliente'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowClienteSelector(true)}
              className="w-full flex items-center justify-between px-4 py-3 border border-zinc-200 rounded-xl bg-white"
              data-testid="select-cliente"
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-zinc-400" />
                <span className="text-zinc-400 text-sm">Público en General</span>
              </div>
              <ChevronDown className="w-4 h-4 text-zinc-400" />
            </button>
          )}
        </div>

        {/* Credit toggle */}
        <div className="mt-4 pt-4 border-t border-zinc-200">
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => setEsCredito(v => !v)}
              className={`w-12 h-7 rounded-full transition-colors relative ${esCredito ? 'bg-blue-600' : 'bg-zinc-300'}`}
              data-testid="toggle-credito"
            >
              <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${esCredito ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className={`w-5 h-5 ${esCredito ? 'text-blue-600' : 'text-zinc-400'}`} />
              <span className={`font-medium ${esCredito ? 'text-blue-600' : 'text-zinc-500'}`}>
                {esAdmin ? 'Venta a crédito' : 'Solicitar venta a crédito'}
              </span>
            </div>
          </label>
          {esCredito && !clienteId && (
            <p className="text-xs text-amber-600 mt-2">
              Selecciona un cliente para registrar a crédito
            </p>
          )}
        </div>
      </div>

      {/* Footer with Total */}
      <div className="border-t border-zinc-200 px-4 py-4 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-zinc-500">{esCredito ? 'Total a crédito' : 'Total a cobrar'}</p>
            <p className="text-3xl font-bold text-zinc-900">{fmtMoney(getTotalMonto())}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-zinc-500">Piezas</p>
            <p className="text-2xl font-bold text-zinc-900">{fmtQty(getTotalPiezas())}</p>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || getTotalPiezas() === 0 || (esCredito && !clienteId)}
          className={`w-full h-14 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 ${
            esCredito ? 'bg-blue-600 active:bg-blue-700' : 'bg-green-600 active:bg-green-700'
          }`}
          data-testid="btn-confirmar-venta"
        >
          {submitting ? (
            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              {esCredito ? <CreditCard className="w-5 h-5" /> : <Check className="w-5 h-5" />}
              {solicitaCredito ? 'Enviar solicitud a administración' : esCredito ? 'Registrar a Crédito' : 'Confirmar Venta'}
            </>
          )}
        </button>
      </div>

      {/* Confirmar contado o crédito cuando hay cliente */}
      {confirmarPago && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="modal-confirmar-pago">
          <div className="w-full max-w-sm bg-white rounded-2xl p-5">
            <h2 className="text-lg font-bold text-zinc-900">¿Esta venta es a crédito o de contado?</h2>
            <p className="text-sm text-zinc-600 mt-2">
              Elegiste a <span className="font-medium text-zinc-900">{clienteSeleccionado?.nombre}</span> pero
              no marcaste crédito. Si el cliente no te paga ahora, elige crédito o te saldrá faltante en el corte.
            </p>
            <p className="text-2xl font-bold text-zinc-900 mt-3">{fmtMoney(getTotalMonto())}</p>
            <div className="mt-5 space-y-2">
              <button
                onClick={() => elegirPago(true)}
                className="w-full h-12 bg-blue-600 active:bg-blue-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                data-testid="btn-pago-credito"
              >
                <CreditCard className="w-5 h-5" />
                {esAdmin ? 'Es a crédito' : 'Es a crédito: enviar solicitud'}
              </button>
              <button
                onClick={() => elegirPago(false)}
                className="w-full h-12 bg-green-600 active:bg-green-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                data-testid="btn-pago-contado"
              >
                <Check className="w-5 h-5" />
                Es de contado: me pagó en efectivo
              </button>
              <button
                onClick={() => setConfirmarPago(false)}
                className="w-full h-11 text-zinc-500 text-sm font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
